import { Box } from "@mui/material";
import { DotLegalHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import PolicyEditableItem from "../policySections/PolicyEditableItem";
import { sectionOuterPaperPadding } from "../policySections/PolicySectionsEditor";
import { PolicyPdfData } from "./PolicyPdf.types";
import PolicyPdfRichText from "./PolicyPdfRichText";

function getInitials(name: string | undefined | null): string {
    if (!name) return "";
    return name
        .split(/\s+/)
        .map((part) => part[0]?.toUpperCase())
        .filter(Boolean)
        .join("");
}

export interface IPolicyPdfContentProps {
    data: PolicyPdfData;
    // Set by the PDF renderer only. On screen the same markup has to survive a phone, which needs scroll
    // containers the headless browser must not get.
    isPdf?: boolean;
}

function PolicyPdfContent({ data, isPdf }: IPolicyPdfContentProps) {
    const { translateString, translateDate } = useTranslation();

    const hasTitle = !!data.title && data.title.trim().length > 0;
    const hasDescription = !!data.description && data.description.trim().length > 0;

    return (
        <Box
            sx={(theme) => ({
                paddingX: sectionOuterPaperPadding,
                color: theme.palette.primary.dark,
            })}
        >
            <Box className="avoid-page-break" sx={{ marginBottom: 3 }}>
                {hasTitle && (
                    <Box
                        className="avoid-page-break"
                        sx={(theme) => ({
                            marginBottom: 1,
                            "& .MuiTypography-root": {
                                fontSize: theme.typography.pxToRem(30),
                            },
                        })}
                    >
                        <DotLegalHeader headerStyle="extraLarge" marginBottom={1}>
                            {data.title}
                        </DotLegalHeader>
                    </Box>
                )}

                {hasDescription && (
                    <Box sx={{ marginBottom: 3 }}>
                        <PolicyPdfRichText html={data.description ?? ""} scrollableTables={!isPdf} />
                    </Box>
                )}
                <Box sx={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 2, fontSize: "14px" }}>
                    <Box>
                        {translateString("version")}: <strong>{data.displayVersion}</strong>
                    </Box>
                    {data.approvedAt && (
                        <Box>
                            {translateString("publishedAt")}: <strong>{translateDate(new Date(data.approvedAt))}</strong>
                        </Box>
                    )}
                    {data.approverName && (
                        <Box>
                            {translateString("approver")}: <strong>{data.approverName}</strong>
                        </Box>
                    )}
                </Box>
            </Box>

            {data.versionHistory.length > 0 && (
                <Box className="page-break-after" sx={{ marginBottom: 3, overflowX: isPdf ? undefined : "auto" }}>
                    <Box
                        component="table"
                        sx={(theme) => ({
                            borderCollapse: "collapse",
                            width: "100%",
                            // Four columns need more room than a phone has, so on screen the table keeps its
                            // width and the wrapper scrolls instead of squeezing the change notes to nothing
                            minWidth: isPdf ? undefined : 480,
                            "& th, & td": {
                                border: `1px solid ${theme.palette.divider}`,
                                padding: 1,
                                textAlign: "left",
                                verticalAlign: "top",
                            },
                            "& td": {
                                fontFamily: theme.typography.fontFamily,
                                fontSize: theme.typography.pxToRem(14),
                            },
                            "& th": { backgroundColor: theme.palette.action.hover, fontWeight: 600 },
                        })}
                    >
                        <Box component="thead">
                            <Box component="tr" sx={(theme) => ({ "& th": { fontSize: theme.typography.pxToRem(14) } })}>
                                <Box component="th">{translateString("version")}</Box>
                                <Box component="th">{translateString("date")}</Box>
                                <Box component="th">{translateString("initials")}</Box>
                                <Box component="th">{translateString("comments")}</Box>
                            </Box>
                        </Box>
                        <Box component="tbody">
                            {data.versionHistory.map((row, i) => (
                                <Box component="tr" key={i} sx={(theme) => ({ "& td": { fontSize: theme.typography.pxToRem(12) } })}>
                                    <Box component="td">{row.displayVersion}</Box>
                                    <Box component="td">{row.approvedAt ? translateDate(new Date(row.approvedAt)) : ""}</Box>
                                    <Box component="td">{getInitials(row.approverName)}</Box>
                                    <Box component="td">{row.changeNotes ?? ""}</Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            )}

            {data.sections.map((section, sectionIndex) => (
                <Box key={section.id} sx={{ marginBottom: 2 }}>
                    <PolicyEditableItem
                        numberLabel={`${sectionIndex + 1}`}
                        title={section.title ?? null}
                        content={section.content ?? null}
                        linkedControlIds={[]}
                        availableControls={[]}
                        controlsHeaderKey="controlsRequiredBySection"
                        setFocusOnTitle={false}
                        dragHandleProps={null}
                        titleFontSize={20}
                        isPdf
                        scrollableTables={!isPdf}
                        onTitleChange={() => {}}
                        onContentChange={() => {}}
                        onLinkedControlsChange={() => {}}
                        onDelete={() => {}}
                        id={section.id}
                    />

                    {section.subsections.map((subsection, subIndex) => (
                        <PolicyEditableItem
                            key={subsection.id}
                            numberLabel={`${sectionIndex + 1}.${subIndex + 1}`}
                            title={subsection.title ?? null}
                            content={subsection.content ?? null}
                            linkedControlIds={[]}
                            availableControls={[]}
                            controlsHeaderKey="controlsRequiredBySubsection"
                            setFocusOnTitle={false}
                            dragHandleProps={null}
                            titleFontSize={18}
                            isPdf
                            scrollableTables={!isPdf}
                            onTitleChange={() => {}}
                            onContentChange={() => {}}
                            onLinkedControlsChange={() => {}}
                            onDelete={() => {}}
                            id={subsection.id}
                        />
                    ))}
                </Box>
            ))}

            {data.changeNotes && (
                <Box className="avoid-page-break" sx={{ marginBottom: 3, marginTop: 3 }}>
                    <DotLegalHeader headerStyle="medium" marginBottom={1}>
                        {translateString("changeNotes")}
                    </DotLegalHeader>
                    <Box sx={{ whiteSpace: "pre-line" }}>{data.changeNotes}</Box>
                </Box>
            )}
        </Box>
    );
}

export default PolicyPdfContent;
